/**
 * CommandPalette — Ctrl K / ⌘K (DESIGN.md §10).
 *
 * Order: the tenant's objects (GET /api/search, matched on the identifiers
 * people have in hand), then actions — the current screen's first — then
 * pages. Empty field: recents + frequent actions. Nothing found: „Adaugă
 * firma «X»”. Opens without animation: it's used from the keyboard, many
 * times a day.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Building2,
  Clock,
  FileText,
  LayoutDashboard,
  PackageOpen,
  Plus,
  Recycle,
  Settings,
  Truck,
  User,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';

import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { SearchInput } from './ui/search-input';
import { cn } from '../lib/utils';
import { SEARCH_ITEMS, scoreItem, type SearchItem } from '../lib/search-items';
import { http } from '../lib/api';
import { normalizeSearch } from '../lib/format-numbers';

const RECENTS_LS_KEY = 'vanagreen:search_recents';
const MAX_RECENTS = 5;

type ObjectType = 'collection' | 'outgoing' | 'treatment' | 'document' | 'company' | 'person' | 'waste';

interface SearchGroup {
  type: ObjectType;
  total: number;
  items: Array<{ id: string; label: string; sub: string; href: string }>;
}

interface SearchResponse {
  success: true;
  data: { groups: SearchGroup[] };
}

const OBJECT_ICONS: Record<ObjectType, LucideIcon> = {
  collection: PackageOpen,
  outgoing: Truck,
  treatment: Wrench,
  document: FileText,
  company: Building2,
  person: User,
  waste: Recycle,
};

// Where „Vezi toate” goes for each group: the list, already searched.
const LIST_HREF: Record<ObjectType, string> = {
  collection: '/operations/collections',
  outgoing: '/operations/outgoing',
  treatment: '/operations/treatment',
  document: '/operations/documents',
  company: '/catalog/companies',
  person: '/catalog/persons',
  waste: '/catalog/wastes',
};

interface Row {
  key: string;
  label: string;
  sub?: string;
  href: string;
  icon: LucideIcon;
  recentId?: string;
  isMuted?: boolean;
}

interface Section {
  title: string;
  rows: Row[];
}

function readRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENTS_LS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function saveRecent(id: string): void {
  try {
    const next = [id, ...readRecents().filter((r) => r !== id)].slice(0, MAX_RECENTS);
    localStorage.setItem(RECENTS_LS_KEY, JSON.stringify(next));
  } catch {
    /* private mode — recents are a convenience */
  }
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<Element | null>(null);

  const debounced = useDebounced(query.trim(), 150);
  const search = useQuery<SearchResponse>({
    queryKey: ['global-search', debounced],
    queryFn: () => http.get<SearchResponse>('/api/search', { query: { q: debounced, limit: 5 } }),
    enabled: open && debounced.length >= 2,
    // Keep the previous results on screen until the new ones arrive.
    placeholderData: (previous) => previous,
    staleTime: 15_000,
  });

  useEffect(() => {
    if (open) {
      returnFocusRef.current = document.activeElement;
      setQuery('');
      setActiveIndex(0);
    }
  }, [open]);

  const staticRows = useMemo(() => {
    const label = (item: SearchItem): string => t(item.labelKey);
    const desc = (item: SearchItem): string => (item.descriptionKey ? t(item.descriptionKey) : '');
    const q = normalizeSearch(query);
    const scored = SEARCH_ITEMS.map((item) => ({
      item,
      score: scoreItem(item, q, normalizeSearch(label(item)), normalizeSearch(desc(item))),
    }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score);
    const toRow = (item: SearchItem): Row => ({
      key: `static:${item.id}`,
      label: label(item),
      sub: desc(item) || undefined,
      href: item.href,
      icon: item.group === 'actions' ? Zap : item.group === 'settings' ? Settings : LayoutDashboard,
      recentId: item.id,
    });
    // Actions of the screen you're on come first („Intrare nouă” on Intrări).
    const section = pathname.split('/').slice(0, 3).join('/');
    const actions = scored
      .filter((x) => x.item.group === 'actions')
      .sort((a, b) => Number(b.item.href.startsWith(section)) - Number(a.item.href.startsWith(section)))
      .map((x) => toRow(x.item));
    const pages = scored.filter((x) => x.item.group !== 'actions').map((x) => toRow(x.item));
    return { actions, pages, byId: new Map(SEARCH_ITEMS.map((i) => [i.id, toRow(i)])) };
  }, [query, t, pathname]);

  const sections: Section[] = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      const recents = readRecents()
        .map((id) => staticRows.byId.get(id))
        .filter((r): r is Row => !!r)
        .map((r) => ({ ...r, key: `recent:${r.key}`, icon: Clock }));
      return [
        { title: t('search.groups.recents'), rows: recents },
        { title: t('search.groups.actions'), rows: staticRows.actions.slice(0, 4) },
      ].filter((s) => s.rows.length > 0);
    }
    const objects: Section[] = (search.data?.data.groups ?? []).map((group) => ({
      title: t(`search.objectGroups.${group.type}`),
      rows: [
        ...group.items.map((item) => ({
          key: `${group.type}:${item.id}`,
          label: item.label,
          sub: item.sub || undefined,
          href: item.href,
          icon: OBJECT_ICONS[group.type],
        })),
        ...(group.total > group.items.length
          ? [
              {
                key: `${group.type}:all`,
                label: t('search.seeAll', { count: group.total }),
                href: `${LIST_HREF[group.type]}?q=${encodeURIComponent(trimmed)}`,
                icon: ArrowRight,
                isMuted: true,
              },
            ]
          : []),
      ],
    }));
    const hasAnything = objects.length > 0 || staticRows.actions.length > 0 || staticRows.pages.length > 0;
    const create: Section[] =
      !hasAnything && debounced.length >= 2 && !search.isFetching
        ? [
            {
              title: t('search.groups.actions'),
              rows: [
                {
                  key: 'create-company',
                  label: t('search.createCompany', { name: trimmed }),
                  href: `/catalog/companies?new=${encodeURIComponent(trimmed)}`,
                  icon: Plus,
                },
              ],
            },
          ]
        : [];
    return [
      ...objects,
      { title: t('search.groups.actions'), rows: staticRows.actions.slice(0, 5) },
      { title: t('search.groups.nav'), rows: staticRows.pages.slice(0, 6) },
      ...create,
    ].filter((s) => s.rows.length > 0);
  }, [query, debounced, search.data, search.isFetching, staticRows, t]);

  const flat = sections.flatMap((s) => s.rows);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${activeIndex}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const close = (): void => {
    onClose();
    const el = returnFocusRef.current;
    if (el instanceof HTMLElement) requestAnimationFrame(() => el.focus());
  };

  const go = (row: Row): void => {
    if (row.recentId) saveRecent(row.recentId);
    onClose();
    navigate(row.href);
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = flat[activeIndex];
      if (row) go(row);
    }
  };

  let index = -1;
  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent
        className="top-[12dvh] max-w-xl translate-y-0"
        bodyClassName="gap-0 p-0"
        isInstant
        onKeyDown={onKeyDown}
        aria-label={t('search.ariaLabel')}
      >
        <DialogTitle className="sr-only">{t('search.ariaLabel')}</DialogTitle>
        <div className="border-b p-3 pr-12">
          <SearchInput
            value={query}
            onValueChange={setQuery}
            placeholder={t('search.placeholder')}
            aria-label={t('search.placeholder')}
            aria-controls="command-palette-list"
            data-testid="command-palette-input"
            autoFocus
          />
        </div>
        <div
          ref={listRef}
          id="command-palette-list"
          role="listbox"
          className="max-h-[min(420px,60dvh)] overflow-y-auto overscroll-contain p-2"
          data-testid="command-palette-list"
        >
          {sections.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">
              {search.isFetching ? t('search.searching') : t('search.empty')}
            </p>
          ) : (
            sections.map((section) => (
              <div key={section.title} className="pb-2">
                <p className="px-3 pb-1 pt-2 text-xs text-muted-foreground">{section.title}</p>
                {section.rows.map((row) => {
                  index += 1;
                  const i = index;
                  const Icon = row.icon;
                  return (
                    <button
                      key={row.key}
                      type="button"
                      role="option"
                      data-index={i}
                      aria-selected={i === activeIndex}
                      onMouseMove={() => setActiveIndex(i)}
                      onClick={() => go(row)}
                      className={cn(
                        'flex min-h-10 w-full items-center gap-3 rounded-md px-3 py-1.5 text-left text-sm',
                        i === activeIndex && 'bg-accent',
                        row.isMuted && 'text-muted-foreground',
                      )}
                    >
                      <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{row.label}</span>
                        {row.sub ? <span className="block truncate text-xs text-muted-foreground">{row.sub}</span> : null}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div className="flex items-center gap-4 border-t px-4 py-2 text-xs text-muted-foreground">
          <span>
            <kbd className="rounded border bg-muted px-1 font-mono">↑↓</kbd> {t('search.hint.navigate')}
          </span>
          <span>
            <kbd className="rounded border bg-muted px-1 font-mono">↵</kbd> {t('search.hint.open')}
          </span>
          <span>
            <kbd className="rounded border bg-muted px-1 font-mono">Esc</kbd> {t('search.hint.close')}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
